import { useEffect, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import type { LFDensityClassRes } from '@quick/go-api-types/services/density_class_linear_feets';

import { axios_demand } from '@/layers/demand/data/fetchers/instances';

import { BOOLEAN_FIELD_ID, TEXT_FIELD_ID } from './constants';
import { useFormDataContext } from './FormDataContext';
import type { IForm } from './types';

export default function CalculateFromPackage({ index }: { index: number }) {
  const { watch, setValue } = useFormContext<IForm>();
  const { serviceType } = useFormDataContext();
  const [lf, densityClass] = useMemo(() => {
    const lf = serviceType?.additional_fields.find(
      ({ field: { field_filter } }) => field_filter === 'linear_feet'
    );
    const density = serviceType?.additional_fields.find(
      ({ field: { field_filter } }) => field_filter === 'density_class'
    );
    return [lf ?? null, density ?? null];
  }, [serviceType]);

  const packagePrefix = `packages.${index}` as const;
  useEffect(() => {
    if (!lf && !densityClass) return;

    const escaped = packagePrefix.replace(/\./, '\\.');
    const re = new RegExp(escaped);

    let timeout: NodeJS.Timeout | undefined = undefined;
    let abort = new AbortController();
    const s = watch((value, { name }) => {
      // Check if the field that changed corresponds to the package and the corresponding fields
      if (
        !name ||
        !re.test(name) ||
        // Weight, volume, stackable
        !['627', '629', '1299', 'units'].some(
          //avoid calculating density class if only total or sub_total has changed
          // because it might be from uom change
          (v) => name.includes(v) && !name.includes('total')
        )
      )
        return;

      // clean previous runs
      clearTimeout(timeout);
      abort.abort();

      timeout = setTimeout(async () => {
        const pkg = value.packages?.[index];
        if (!pkg || !pkg.f) return;

        let allFilled = true;

        const entries = Object.entries(pkg.f).map(([k, v]) => {
          const originalK = k
            .replace('_', '')
            .replace(TEXT_FIELD_ID, '')
            .replace(BOOLEAN_FIELD_ID, '');
          if (typeof v !== 'object') return [originalK, v];
          // Check for numeric field
          if (v.dimensions) {
            // Check if all numeric fields have been filled
            Object.entries(v.dimensions).forEach(([_, dimension]) => {
              if (!dimension) allFilled = false;
            });
          }
          return [originalK, v];
        });

        if (allFilled) {
          abort = new AbortController();
          let time = 2_000;

          while (!abort.signal.aborted)
            try {
              const { data } = await axios_demand.post<LFDensityClassRes>(
                '/services/density_class_linear_feets',
                {
                  f: Object.fromEntries(
                    entries.filter(
                      ([k]) => !['1800', '1801'].includes(k?.toString() ?? '')
                    )
                  ),
                  units: pkg.units,
                },
                { signal: abort.signal }
              );
              if (densityClass)
                setValue(
                  `${packagePrefix}.f.${densityClass.field.id}_`,
                  data.density_class.toString() as never,
                  { shouldDirty: true }
                );

              if (lf)
                setValue(
                  `${packagePrefix}.f.${lf.field.id}_${TEXT_FIELD_ID}`,
                  data.linear_feets.toString() as never,
                  { shouldDirty: true }
                );
              break;
            } catch (_err) {
              await new Promise((r) => {
                setTimeout(() => r(void 0), time);
              });
              time = time * time; // Exp backoff
            }
        }
      }, 1_000);
    });

    return () => {
      clearTimeout(timeout);
      abort.abort();
      s.unsubscribe();
    };
  }, [watch, index, packagePrefix, lf, densityClass, setValue]);

  return null;
}
